import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

function AuditLogI18n({ classes, productName }: { classes: ReturnType<typeof styles>; productName?: string }) {
  const formatMessage = useFormatMessage('ess.help_audit_log')
  return (
    <div className={classes.content} data-testid="content">
      <p>{formatMessage('intro_p1', { productName })}</p>
      <p>{formatMessage('intro_p2')}</p>
      <p>{formatMessage('intro_p3')}</p>
      <ul>
        <li>
          <b>{formatMessage('fields.date_time.label')}</b> – {formatMessage('fields.date_time.description')}
        </li>
        <li>
          <b>{formatMessage('fields.user.label')}</b> – {formatMessage('fields.user.description')}
        </li>
        <li>
          <b>{formatMessage('fields.scope.label')}</b> – {formatMessage('fields.scope.description')}
        </li>
        <ul>
          <li>
            <b>{formatMessage('fields.scope.items.account.label')}</b> –{' '}
            {formatMessage('fields.scope.items.account.description')}
          </li>
          <li>
            <b>{formatMessage('fields.scope.items.domain.label')}</b> –{' '}
            {formatMessage('fields.scope.items.domain.description')}
          </li>
          <li>
            <b>{formatMessage('fields.scope.items.user.label')}</b> –{' '}
            {formatMessage('fields.scope.items.user.description')}
          </li>
        </ul>
        <li>
          <b>{formatMessage('fields.details.label')}</b> – {formatMessage('fields.details.description')}
        </li>
      </ul>
    </div>
  )
}

function AuditLog(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <AuditLogI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <p>
        The Audit Log provides a record of all changes made to {props.productName} by administrators, end users, and
        Barracuda Support.{' '}
      </p>
      <p>The Audit Log contains data for the last 14 days.</p>
      <p>The Audit Log displays the following information:</p>
      <ul>
        <li>
          <b>Date/Time</b> – When the action occurred.
        </li>
        <li>
          <b>User</b> – Email and IP address of the user who performed the action. Displayed value is{' '}
          <b>Barracuda Networks Support</b> if action was taken by a Barracuda Support Representative.{' '}
        </li>
        <li>
          <b>Scope</b> – Describes what is affected by the action. Actions include (creating, changing, or deleting).{' '}
        </li>
        <ul>
          <li>
            <b>Account</b> – Affects Account Settings, Domains, Marking Messages, Allow Lists.
          </li>
          <li>
            <b>Domain</b> – Affects Domain Settings, Users, Allow Lists.
          </li>
          <li>
            <b>User</b> – Affects User Settings.{' '}
          </li>
        </ul>
        <li>
          <b>Details</b> – Basic information about the action.{' '}
        </li>
      </ul>
    </div>
  )
}

export default AuditLog
